/**
 * Single source of truth for the connector catalog. Owns display label,
 * brand icon path, short description, value-add bullets, connection-method
 * label, and current availability status.
 *
 * Consumers: ConnectorList (renders Connected / Available / Coming-soon
 * sections), ConnectorForm (offers only `available` types in its picker),
 * and any future per-connector deep-dive pages.
 *
 * Adding a connector: append an entry below. Promoting "upcoming" →
 * "available" only requires flipping `status` and ensuring the wizard +
 * MCP server are wired in `services/connector/`.
 */

import microsoftIcon from '../assets/connector-icons/microsoft.svg';
import oktaIcon from '../assets/connector-icons/okta.svg';
import splunkIcon from '../assets/connector-icons/splunk.svg';
import awsIcon from '../assets/connector-icons/aws.svg';
import crowdstrikeIcon from '../assets/connector-icons/crowdstrike.svg';
import wizIcon from '../assets/connector-icons/wiz.svg';
import paloaltoIcon from '../assets/connector-icons/paloalto.svg';
import snykIcon from '../assets/connector-icons/snyk.svg';
import qualysIcon from '../assets/connector-icons/qualys.svg';
import genericIcon from '../assets/connector-icons/generic.svg';

export type ConnectorStatus = 'available' | 'upcoming';

export interface ConnectorCatalogEntry {
  /** Canonical productType — must match `Connector.productType` in the DB. */
  type: string;
  label: string;
  status: ConnectorStatus;
  /**
   * URL of the brand SVG (statically imported, resolved by Vite at build).
   * Falls back to `genericIcon` for entries that don't have a dedicated
   * brand mark — `getCatalogIcon` should be used by the UI rather than
   * reading this field directly so the fallback is always honored.
   */
  iconPath: string;
  /** One-line summary shown on the card. */
  shortDescription: string;
  /** 2–3 bullets shown in expanded details. */
  valueAdd: string[];
  /**
   * Human-readable label for the underlying API (e.g. "Microsoft Graph +
   * Defender API"). Shown in the Connected card's expanded metadata.
   * Optional for upcoming entries that aren't wired yet.
   */
  connectionMethod?: string;
  /**
   * Legacy productType strings that should resolve to this entry. Lets us
   * fold rolled-up vendors (e.g. `sentinel` → `microsoft`) without a DB
   * migration of existing rows.
   */
  legacyAliases?: string[];
}

export const CONNECTOR_CATALOG: readonly ConnectorCatalogEntry[] = [
  {
    type: 'microsoft',
    label: 'Microsoft',
    status: 'available',
    iconPath: microsoftIcon,
    shortDescription: 'Entra ID, Defender XDR, Sentinel, Graph',
    valueAdd: [
      'Identity threat detection (BEC/AiTM)',
      'Sign-in forensics and Conditional Access posture',
      'KQL hunts across Defender + Sentinel',
    ],
    connectionMethod: 'Microsoft Graph + Defender API',
    legacyAliases: ['sentinel'],
  },
  {
    type: 'okta',
    label: 'Okta',
    status: 'available',
    iconPath: oktaIcon,
    shortDescription: 'Identity provider for users, groups, sessions, MFA',
    valueAdd: [
      'User and group lookup with SCIM filters',
      'Active session and MFA factor inspection',
      'System log queries for identity events',
    ],
    connectionMethod: 'Okta OAuth2 JWT',
  },
  {
    type: 'splunk',
    label: 'Splunk',
    status: 'available',
    iconPath: splunkIcon,
    shortDescription: 'SPL search across indexes and notable events',
    valueAdd: [
      'Run SPL queries against any index',
      'Read and update Enterprise Security notables',
      'Inspect saved searches and indexes',
    ],
    connectionMethod: 'Splunk REST API',
  },
  {
    type: 'aws',
    label: 'AWS',
    status: 'available',
    iconPath: awsIcon,
    shortDescription: 'CloudTrail, GuardDuty, IAM',
    valueAdd: [
      'Cloud control-plane forensics via CloudTrail',
      'GuardDuty findings triage',
      'IAM posture and identity inspection',
    ],
    connectionMethod: 'AWS SDK',
  },
  {
    type: 'crowdstrike',
    label: 'CrowdStrike Falcon',
    status: 'upcoming',
    iconPath: crowdstrikeIcon,
    shortDescription: 'EDR detections, hosts, and incident timelines',
    valueAdd: [
      'Endpoint detection enrichment',
      'Host containment workflows',
      'Process tree pivots',
    ],
  },
  {
    type: 'wiz',
    label: 'Wiz',
    status: 'upcoming',
    iconPath: wizIcon,
    shortDescription: 'Cloud security posture and toxic combinations',
    valueAdd: [
      'CSPM findings',
      'Attack path analysis',
      'Cloud asset inventory',
    ],
  },
  {
    type: 'paloalto',
    label: 'Palo Alto Networks',
    status: 'upcoming',
    iconPath: paloaltoIcon,
    shortDescription: 'Network telemetry and Cortex XDR',
    valueAdd: [
      'Network detection enrichment',
      'Cortex incident pivots',
      'Firewall log queries',
    ],
  },
  {
    type: 'snyk',
    label: 'Snyk',
    status: 'upcoming',
    iconPath: snykIcon,
    shortDescription: 'Application and dependency vulnerabilities',
    valueAdd: [
      'Vuln context for code',
      'Container and IaC scanning',
      'License risk',
    ],
  },
  {
    type: 'qualys',
    label: 'Qualys',
    status: 'upcoming',
    iconPath: qualysIcon,
    shortDescription: 'Vulnerability management and compliance',
    valueAdd: [
      'Asset vuln posture',
      'Compliance scan results',
      'Patch prioritization',
    ],
  },
  {
    type: 'microsoft-teams',
    label: 'Microsoft Teams',
    status: 'upcoming',
    // Reuse the Microsoft icon for Teams; a Teams-specific icon can
    // ride on a follow-up.
    iconPath: microsoftIcon,
    shortDescription: 'Cade in your Teams workspace — same model as Slack',
    valueAdd: [
      'Cade messaging in Teams channels',
      'Investigations posted to chat',
      '[ACTION_REQUIRED] approval flows in Teams',
    ],
    legacyAliases: ['teams', 'msteams'],
  },
];

/**
 * Resolve a productType (including legacy aliases) to its catalog entry.
 * Returns undefined for unknown types — UI should fall back to a generic
 * card rather than crashing.
 */
export function getCatalogEntry(productType: string): ConnectorCatalogEntry | undefined {
  return CONNECTOR_CATALOG.find(
    (e) => e.type === productType || (e.legacyAliases?.includes(productType) ?? false),
  );
}

export function getAvailableTypes(): readonly ConnectorCatalogEntry[] {
  return CONNECTOR_CATALOG.filter((e) => e.status === 'available');
}

export function getUpcomingTypes(): readonly ConnectorCatalogEntry[] {
  return CONNECTOR_CATALOG.filter((e) => e.status === 'upcoming');
}

/**
 * Get the icon URL for a productType, falling back to the generic icon
 * for unknown types or entries with no brand mark wired up. Use this from
 * the UI rather than reading `entry.iconPath` directly.
 */
export function getCatalogIcon(productType: string): string {
  const entry = getCatalogEntry(productType);
  return entry?.iconPath || genericIcon;
}
